import React, { use, useEffect, useState } from "react";
import styled from "styled-components";
import { FaTemperatureHigh } from "react-icons/fa6";
import { LuWeight } from "react-icons/lu";
import { GiFilmSpool } from "react-icons/gi";
import { CiEdit } from "react-icons/ci";
import { BsCreditCard2Front } from "react-icons/bs";
import InputField from "./InputField.tsx";
import { SpoolSVG } from "../../../assets/SpoolSVG.tsx";
import { IoMdCloseCircleOutline } from "react-icons/io";
import { TfiSave } from "react-icons/tfi";
import { TbPlugConnected, TbPlugConnectedX } from "react-icons/tb";
import Button from "./Button.tsx";
import InputDropdown from "./InputDropdown.tsx";
import { useQuery } from "@tanstack/react-query";
import { getFilamentTypes } from "../../../services/productsService.js";
import { IoColorFillOutline } from "react-icons/io5";
import {
  getSuppliers,
  getColors,
  addNewMaterialType,
  addNewFilament,
  addNewColor,
} from "../../../services/filamentService.js";
import SimpleDropdown from "./SimpleDropdown.tsx";
import nearestColor from "nearest-color";
import { colornames } from "color-name-list";
import { useArduino } from "../../../hooks/useArduino.jsx";
import Loader from "./Loader.tsx";

const Wrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;

  padding: 1rem 2rem;
  border-radius: 8px;
  align-items: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  background-color: white;
`;
const Container = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  padding-top: 1rem;

  @media (max-width: 768px) {
    flex-direction: column;
    min-width: 200px;
  }
`;

const DisplayContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
`;
const Spool = styled.div<{ color?: string }>`
  width: 85px;
  height: 85px;
  border-radius: 50%;
  margin-bottom: 20px;
`;

const ColorName = styled.div`
  color: var(--secondary-font-color);
  font-weight: 500;
  font-size: 14px;
  text-transform: uppercase;
  margin-top: 50px;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

const ColorCode = styled.div`
  color: var(--tertiary-font-color);
  padding-top: 4px;
  padding-bottom: 4px;
  font-size: 12px;
  width: 100%;
`;

const SettingWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
  border-left: 1px solid rgba(0, 0, 0, 0.15);
  padding-top: 20px;
  padding-left: 20px;
  gap: 1rem;
  width: 100%;

  @media (max-width: 768px) {
    border-left: none;
    border-top: 1px solid rgba(0, 0, 0, 0.15);
    padding-left: 0;
  }
`;

const Buttons = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  gap: 1rem;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  cursor: pointer;
  background-color: transparent;
  font-size: 1.5rem;
  color: var(--tertiary-font-color);
  border: none;
  outline: none;
`;

const EditButton = styled.button`
  cursor: pointer;
  background-color: transparent;
  font-size: 1.5rem;
  color: var(--tertiary-font-color);
  border: none;
  outline: none;
`;

const EditNameContainer = styled.div`
  margin-top: 50px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export type FilamentConfig = {
  material: string;
  supplierName: string;
  spoolWeight: number;
  weight: number;
  temperature: number;
  filamentID: number;
};

interface Props {
  closeAddMode: (string) => void;
}

const AddFilamentComponent: React.FC<Props> = ({ closeAddMode }) => {
  const [isColorEdit, setIsColorEdit] = useState(false);
  const [isError, setIsError] = useState(false);

  const [colorCode, setColorCode] = useState("#FFFFFF");
  const [colorName, setColorName] = useState("White");
  const [weight, setWeight] = useState("");
  const [temperature, setTemperature] = useState("");
  const [type, setType] = useState({ value: "-1", label: "" });
  const [supplier, setSupplier] = useState("");
  const [scanCommand, setScanCommand] = useState(0);
  const [isDeviceReady, setIsDeviceReady] = useState(false);
  const [isDeviceLoading, setIsDeviceLoading] = useState(false);
  const {
    requestSerialPort,
    disconnectArduino,
    sendCommand,
    output,
    connected,
  } = useArduino();
  useEffect(() => {
    return () => {
      disconnectArduino(); // Cleanup on unmount
    };
  }, []);

  const { data: types } = useQuery({
    queryKey: ["types"],
    queryFn: () => getFilamentTypes(),
  });
  const { data: suppliers } = useQuery({
    queryKey: ["suppliers"],
    queryFn: () => getSuppliers(),
    onSuccess: (data) => {
      if (data && data.length > 0) {
        if (supplier === "") {
          setSupplier(data[0].supplierID);
        }
      }
    },
  });

  const { data: colors } = useQuery({
    queryKey: ["colors"],
    queryFn: () => getColors(),
  });

  const handleAddType = async (typeName: string) => {
    try {
      const response = await addNewMaterialType(typeName);
      return response.typeID;
    } catch (error) {
      console.error("Error creating type:", error);
    }
  };

  const handleAddColor = async (color) => {
    try {
      const response = await addNewColor(color);
      return response.colorCode;
    } catch (error) {
      console.error("Error creating color:", error);
    }
  };

  const handleSave = async () => {
    // Save the filament to the database
    console.log(supplier);
    if (
      colorCode &&
      colorName &&
      weight &&
      temperature &&
      type &&
      type.label &&
      type.label !== " " &&
      supplier
    ) {
      setIsError(false);
      let typeID = type.value;
      let colorID = colorCode;
      if (type.value === "-1") {
        // daca nu exista in baza de date tipul de filament il adaugam

        typeID = await handleAddType(type.label);
      }
      if (
        colors.filter((color) => color.colorCode === colorCode).length === 0
      ) {
        // daca nu exista culoarea in baza de date o adaugam
        colorID = await handleAddColor({ colorCode, colorName });
      }

      const filament = {
        colorCode: colorID,
        weight,
        temperature,
        typeID,
        supplierID: supplier,
      };

      try {
        await addNewFilament(filament);
        closeAddMode("success");
      } catch (error) {
        closeAddMode("error");
        console.error("Error creating type:", error);
      }
    } else {
      setIsError(true);
    }
  };

  useEffect(() => {
    if (output) {
      console.log("Output from Arduino:", output);
      if (output?.trim() == "n" || output?.trim() == "on") {
        console.log(connected);
        sendCommand("getAvailable");
        setIsDeviceReady(true);
      } else if (output.includes("color") && scanCommand === 1) {
        setIsDeviceLoading(false);
        handleScan(output.toString().replace("color:", "").trim());
      } else if (output.includes("mass") && scanCommand === 2) {
        setIsDeviceLoading(false);
        const outputMass = output.toString().replace("mass:", "").trim();
        if (isNaN(Number(outputMass))) {
          alert("Invalid weight value received from device");
          return;
        } else {
          let resultWeight = Number(outputMass);
          if (supplier && suppliers) {
            const supplierSpool = suppliers.find(
              (s) => s.supplierID === supplier
            )?.spoolWeight;
            if (supplierSpool) {
              resultWeight = resultWeight - supplierSpool;
            }
          }

          setWeight(resultWeight.toString());
        }
      } else if (output.includes("error")) {
        setIsDeviceLoading(false);
        alert("S-a produs o eroare la citire");
      }
    }
  }, [scanCommand, output]);
  const handleScan = (color) => {
    // Scan the filament and get color code
    // const hardcodedColorCode = "#348ceb";
    if (!color || color === "" || color.length < 3)
      return alert("Invalid color code");
    setColorCode(color);

    //find the color name from the color code

    // nearestColor need objects {name => hex} as input
    const colors = colornames.reduce(
      (o, { name, hex }) => Object.assign(o, { [name]: hex }),
      {}
    );

    const nearest = nearestColor.from(colors);

    const nearestColorMatch = nearest(color);
    const autoName =
      typeof nearestColorMatch === "object" && "name" in nearestColorMatch
        ? nearestColorMatch.name
        : nearestColorMatch;

    //set the color name found
    setColorName(autoName);
    setIsColorEdit(false);
  };

  const handleWeightScan = () => {
    setIsDeviceLoading(true);
    sendCommand("getWeight");
    setScanCommand(2);
  };

  const handleColorScan = () => {
    setIsDeviceLoading(true);
    sendCommand("getColor");
    setScanCommand(1);
  };

  return (
    <Wrapper data-testid="add-filament-tile">
      <Container>
        <DisplayContainer data-testid="display-settings">
          <Spool>
            <SpoolSVG color={colorCode} />
          </Spool>
          {!isColorEdit && (
            <>
              <ColorName>
                {colorName}
                <EditButton
                  onClick={() => {
                    setIsColorEdit(true);
                  }}
                >
                  <CiEdit />
                </EditButton>
              </ColorName>

              <ColorCode>{colorCode}</ColorCode>
            </>
          )}

          {isColorEdit && (
            <EditNameContainer>
              <InputDropdown
                values={
                  colors &&
                  colors.map((color) => ({
                    value: color.colorCode,
                    label: color.colorName,
                  }))
                }
                name="name"
                currentValue={{ value: colorCode, label: colorName }}
                placeholder="White"
                onChange={({ value, label }) => {
                  setColorName(label);
                  if (value !== "-1") setColorCode(value);
                }}
              />

              <InputField
                name="code"
                type="text"
                value={colorCode}
                onChange={(e) => setColorCode(e.target.value)}
                isRequired
                placeholder="#FFFFFF"
              />
            </EditNameContainer>
          )}
          <CloseButton
            onClick={() => {
              closeAddMode("abort");
            }}
          >
            <IoMdCloseCircleOutline />
          </CloseButton>
        </DisplayContainer>

        <SettingWrapper data-testid="filament-settings">
          <InputDropdown
            values={
              types &&
              types.map((type) => ({
                value: type.id,
                label: type.label,
              }))
            }
            name="type"
            placeholder="PLA"
            icon={<GiFilmSpool />}
            onChange={({ value, label }) => setType({ value, label })}
          />

          <SimpleDropdown
            values={
              suppliers &&
              suppliers.map((supplier) => ({
                value: supplier.supplierID,
                label: supplier.supplierName,
              }))
            }
            name="supplier"
            icon={<BsCreditCard2Front />}
            onChange={({ value, label }) => setSupplier(value)}
          />
          <InputField
            name="type"
            type="number"
            value={temperature}
            onChange={(e) => setTemperature(e.target.value)}
            isRequired
            placeholder="200°C"
            icon={<FaTemperatureHigh />}
          />
          <InputField
            name="weight"
            type="text"
            value={weight}
            onChange={(e) => setWeight(e.target.value)}
            isRequired
            placeholder="200"
            icon={<LuWeight />}
          />
          {isError && (
            <p style={{ color: "#c72418", margin: 0 }}>
              *Câmpuri necompletate !
            </p>
          )}
          <Buttons>
            {connected ? (
              <>
                <Button
                  id="disconnect-arduino-button"
                  onClick={() => {
                    disconnectArduino();
                  }}
                  icon={<TbPlugConnectedX />}
                  backgroundColor={"white"}
                  outline="var(  --primary-brown)"
                  color="var(  --primary-brown)"
                />
              </>
            ) : (
              <Button
                id="connect-arduino-button"
                onClick={async () => {
                  try {
                    await requestSerialPort();
                  } catch (error) {
                    console.error("Eroare la conectare:", error);
                  }
                }}
                icon={<TbPlugConnected />}
                backgroundColor={"white"}
                outline="var(--secondary-green)"
                color="var(--secondary-green)"
              />
            )}
            {connected && !isDeviceReady && <Loader isSmall />}
            {connected &&
              isDeviceReady &&
              (isDeviceLoading ? (
                <Loader isSmall />
              ) : (
                <>
                  <Button
                    id="scan-wight"
                    disabled={!connected}
                    onClick={handleWeightScan}
                    icon={
                      <div
                        style={{
                          fontSize: "1.5rem",
                          alignContent: "center",
                          display: "flex",
                        }}
                      >
                        <LuWeight />
                      </div>
                    }
                    backgroundColor={"white"}
                    outline="var(--secondary-green)"
                    color="var(--secondary-green)"
                  />
                  <Button
                    id="scan-color"
                    onClick={handleColorScan}
                    disabled={!connected}
                    icon={
                      <div
                        style={{
                          fontSize: "1.5rem",
                          alignContent: "center",
                          display: "flex",
                        }}
                      >
                        <IoColorFillOutline />
                      </div>
                    }
                    backgroundColor={"white"}
                    outline="var(--secondary-green)"
                    color="var(--secondary-green)"
                  />
                </>
              ))}

            <Button
              id="save-filament"
              onClick={handleSave}
              text="SAVE"
              icon={<TfiSave />}
              backgroundColor={"var(--secondary-green)"}
              color="white"
            />
          </Buttons>
        </SettingWrapper>
      </Container>
    </Wrapper>
  );
};

export default AddFilamentComponent;
